import React, { ChangeEventHandler, FocusEventHandler, ReactNode } from 'react';
import { Box, TextField, Tooltip } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { FormikTouched } from 'formik/dist/types';

type Props = {
    label: string;
    name: string;
    placeholder?: string;
    size?: 'small' | 'medium';
    margin?: 'dense' | 'normal' | 'none';
    required?: boolean;
    value?: string;
    rows?: number;
    touched?: boolean | FormikTouched<any> | FormikTouched<any>[] | undefined;
    errors?: unknown;
    disabled?: boolean;
    infoTooltipText: ReactNode;
    infoTooltipWidth?: string | number;
    onChange?: ChangeEventHandler<HTMLInputElement | HTMLTextAreaElement>;
    onBlur?: FocusEventHandler<HTMLInputElement | HTMLTextAreaElement>;
    inputProps?: React.InputHTMLAttributes<HTMLInputElement>;
    maxLength?: number;
};

const fieldSx = {
    '& label.Mui-focused': {
        color: 'primary.main',
    },
    '& .MuiInput-underline:after': {
        borderBottomColor: 'primary.main',
    },
    '& .MuiFilledInput-underline:after': {
        borderBottomColor: 'primary.main',
    },
    '& .MuiOutlinedInput-root': {
        '&.Mui-focused fieldset': {
            borderColor: 'primary.main',
        },
    },
};

const tooltipSlotProps = (width: string | number) => ({
    tooltip: {
        sx: {
            maxWidth: width,
            bgcolor: 'grey.800',
            color: '#fff',
            fontSize: '0.8125rem',
            lineHeight: 1.45,
            p: 1.25,
            '& .MuiTooltip-arrow': {
                color: 'grey.800',
            },
        },
    },
});

export const FieldTextWithInfoTooltip = ({
    label,
    name,
    placeholder,
    size = 'small',
    margin,
    required,
    rows,
    touched,
    errors,
    value,
    disabled,
    infoTooltipText,
    infoTooltipWidth = 300,
    onChange,
    onBlur,
    inputProps,
    maxLength,
}: Props) => {
    const resolvedMaxLength = maxLength ?? inputProps?.maxLength;

    const fieldLabel = (
        <Box
            component="span"
            sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25 }}
        >
            {label}
            <Tooltip
                title={infoTooltipText}
                arrow
                placement="top-start"
                enterTouchDelay={0}
                componentsProps={tooltipSlotProps(infoTooltipWidth)}
            >
                <Box
                    component="span"
                    aria-label="Información del campo"
                    sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        lineHeight: 0,
                        cursor: 'pointer',
                    }}
                    onMouseDown={(event) => event.preventDefault()}
                >
                    <InfoOutlinedIcon
                        sx={{ fontSize: 16, color: 'primary.main' }}
                    />
                </Box>
            </Tooltip>
        </Box>
    );

    return (
        <TextField
            id={`field-${name}`}
            fullWidth
            multiline={!!rows}
            placeholder={placeholder}
            label={fieldLabel}
            name={name}
            value={value}
            required={required}
            rows={rows}
            margin={margin}
            disabled={disabled}
            onChange={onChange}
            onBlur={onBlur}
            size={size}
            helperText={
                touched && errors
                    ? String(errors)
                    : resolvedMaxLength
                        ? `${value?.length || 0}/${resolvedMaxLength} caracteres`
                        : ''
            }
            error={Boolean(touched && errors)}
            InputLabelProps={{
                sx: {
                    display: 'inline-flex',
                    alignItems: 'center',
                    bgcolor: 'background.paper',
                    px: 0.5,
                    pointerEvents: 'auto',
                    zIndex: 1,
                },
            }}
            sx={fieldSx}
            InputProps={{ notched: false }}
            inputProps={{
                maxLength: resolvedMaxLength,
                ...inputProps,
            }}
        />
    );
};
